import {ChangeDetectionStrategy, Component, computed, ElementRef, Inject, Injectable, InjectionToken, input, signal, TemplateRef, viewChild} from "@angular/core";
import {ArkivdokumentModell, ArkivnodeId, ArkivRessursClient, DokumentId, DokumentType, DokumentVersjonModell, DokumentVersjonOgSakerModell, SigneringStatus, TynnSakModell, VirussjekkStatus} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatMenu, MatMenuItem} from "@angular/material/menu";
import {MatIcon} from "@angular/material/icon";
import {TranslateModule} from "@ngx-translate/core";
import {MatDivider} from "@angular/material/divider";
import {BekreftKlikkDirektiv} from "web-ng2/delt/direktiver/button--bekreftKlikk/button--bekreftKlikk";
import {DokumentFormatIkonKomponent} from "web-ng2/moduler/dokument/komponenter/dokument-format-ikon/dokument-format-ikon";
import {erArkivdokument, erArkivdokumentdokumentISak, erProtokolldokumentISak, erSaksdokument, erSkjermbartSaksdokument, Signatarinformasjon} from "felles-ng2/modell/dokumentModellHjelpere";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {åpneINyttvindu} from "felles-ng2/støttefunksjoner/nettleser";
import {MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle} from "@angular/material/dialog";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {erTomListe} from "felles-ng2/støttefunksjoner/lister";
import {SkjermingDialogKomponent} from "web-ng2/moduler/skjerming/modale/skjerming-dialog/skjerming-dialog";
import {MatTooltip} from "@angular/material/tooltip";
import {ArkivFlyttNodeDialogKomponent} from "web-ng2/moduler/arkiv/modale/arkiv-flytt-node-dialog/arkiv-flytt-node-dialog";
import {ArkivDatatjeneste} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {MatButton} from "@angular/material/button";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {DokumentFormatInfo, DokumentInformasjonTjeneste} from "felles-ng2/tjenester/DokumentInformasjonTjeneste";
import {DokumentSigneringTjeneste} from "web-ng2/moduler/dokument/tjenester/DokumentSigneringTjeneste";
import {ÅpneDokumentTjeneste} from "web-ng2/moduler/dokument/tjenester/ÅpneDokumentTjeneste";
import {AgendaDokumentValgDialogKomponent} from "web-ng2/moduler/dokument/modale/agenda-dokument-valg-dialog/agenda-dokument-valg-dialog";
import {ForfalteOppgaverDokumentValgDialogKomponent} from "web-ng2/moduler/dokument/modale/forfalte-oppgaver-dokument-valg-dialog/forfalte-oppgaver-dokument-valg-dialog";
import {FavorittDatatjeneste} from "felles-ng2/tjenester/datalager/FavorittDatatjeneste";

const debug = initDebugger('DokumentMenyKomponent');

/**  InjectionToken for injisering av {@link DelegerteDokumentMenyOperasjoner} implementasjon. */
export const DelegerteDokumentMenyOperasjoner_DI_TOKEN = new InjectionToken<DelegerteDokumentMenyOperasjoner>('DelegerteDokumentMenyOperasjoner implementasjon');

/**
 * Meny for dokumenter, delt av {@link DokumentListevisningKomponent} og {@link DokumentKortvisningKomponent}
 */
@Component({
    selector: 'dokument-meny',
    templateUrl: 'dokument-meny.html',
    styleUrl: 'dokument-meny.scss',
    imports: [
        MatMenu,
        MatIcon,
        TranslateModule,
        MatMenuItem,
        MatDivider,
        BekreftKlikkDirektiv,
        DokumentFormatIkonKomponent,
        MatTooltip,
        FormaterPlainDatePipe,
        MatButton,
        MatDialogActions,
        MatDialogClose,
        MatDialogContent,
        MatDialogTitle,
        ZonedDateTimeSomPlainDatePipe
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DokumentMenyKomponent {

    matMeny = viewChild.required(MatMenu);
    private dokumentKanIkkeSlettesDialogTemplate = viewChild.required<TemplateRef<ElementRef>>('dokumentKanIkkeSlettesDialogTemplate');

    readonly dokumentversjon = input.required<DokumentVersjonModell>();
    readonly signatarinformasjon = input.required<Signatarinformasjon>();

    readonly informasjon = computed(() => this.dokumentInformasjon.dokumentinformasjon(this.dokumentversjon()));
    readonly erFavoritt = computed(() => this.avhengigheter.erFavoritt(this.dokumentversjon()));
    readonly tilgjengeligeMenyinnslag = computed(() => this.beregnTilgjengeligeMenyinnslag())
    readonly kanIkkeSlettesPgaBrukISaker = signal<DokumentVersjonOgSakerModell[]>([]);

    erVisPreview = false;

    constructor(private avhengigheter: DokumentMenyKomponentAvhengigheter,
                @Inject(DelegerteDokumentMenyOperasjoner_DI_TOKEN) private delegerteOperasjoner: DelegerteDokumentMenyOperasjoner,
                private dokumentInformasjon: DokumentInformasjonTjeneste,
                private dokumentSigneringTjeneste: DokumentSigneringTjeneste,
                private dokumentTjeneste: ÅpneDokumentTjeneste,
                private navigasjon: Navigasjon,
                private matDialog: MatDialog) {
    }

    private beregnTilgjengeligeMenyinnslag() {
        const tilgjengelig = this.tilgjengeligFunksjonalitet();
        return {
            nyVersjon:
              tilgjengelig.rediger &&
              !this.informasjon().erGenerert &&
              this.dokumentversjon().signeringStatus == SigneringStatus.ikkeAktivert,
            flytt: tilgjengelig.rediger && erArkivdokument(this.dokumentversjon()),
            slett: tilgjengelig.rediger,
            velgDokumentInnhold: /*this.harSkrivetilgang()*/ tilgjengelig.rediger && [DokumentType.generertAgendaDokument, DokumentType.generertForfalteOppgaverDokument].includes(this.dokumentversjon().type),
            inlineVisning: this.dokumentversjon().virussjekkStatus != VirussjekkStatus.infisert && this.dokumentversjon().harPdfRepresentasjon,
            redigerSkjerming: tilgjengelig.skjerming && (erSkjermbartSaksdokument(this.dokumentversjon()) || this.dokumentversjon().type === DokumentType.arkivdokument),
            skalSignere: this.signatarinformasjon().skalSignere,
            sendTilSignering: tilgjengelig.signering && this.dokumentSigneringTjeneste.sendTilSigneringMulig(this.dokumentversjon()),
            visSignatarer: tilgjengelig.signering && this.dokumentversjon().signeringStatus != SigneringStatus.ikkeAktivert,
            avsluttSignering: tilgjengelig.signering && this.dokumentSigneringTjeneste.avsluttSigneringMulig(this.dokumentversjon()),
            redigerSignatarer: tilgjengelig.signering && this.dokumentSigneringTjeneste.redigerSignatarerMulig(this.dokumentversjon()),
            favorittMarkering: tilgjengelig.favorittmarkering,
            erFavoritt: this.avhengigheter.erFavoritt(this.dokumentversjon()),
            visVersjoner: tilgjengelig.versjonsvisning && this.dokumentversjon().versjonsnummer > 1,
            //dropper denne foreløpig - den elastic-search baserte funksjonen for å finne liknende dokumenter fungerer dårlig - kanskje med fremtidig semantisk/ai søk vil dette igjen kunne bli aktuelt
            visLiknendeDokumenter: false,
            visBehandlesISaker: tilgjengelig.behandlesISaker &&
              [DokumentType.arkivdokumentISak, DokumentType.arkivdokument].includes(this.dokumentversjon().type),
            visIArkiv: erArkivdokumentdokumentISak(this.dokumentversjon()),
            visGaaTilProtokoll: erProtokolldokumentISak(this.dokumentversjon()),
            aapneIGoogleDrive: erIkkeTomStreng(this.dokumentversjon().googleDriveUrl),
            aapneIMicrosoftOneDrive: erIkkeTomStreng(this.dokumentversjon().microsoftOneDriveUrl)
        }
    }

    redigeringMulig() {
        return this.tilgjengeligFunksjonalitet().rediger;
    }

    registrerNyVersjon() {
        this.delegerteOperasjoner.registrerNyDokumentversjon(this.dokumentversjon());
    }

    velgDokumentInnhold() {
        if (this.dokumentversjon().type === DokumentType.generertAgendaDokument)
            return this.velgAgendaDokumentInnhold();

        if (this.dokumentversjon().type === DokumentType.generertForfalteOppgaverDokument)
            return this.velgForfalteOppgaverDokumentInnhold();
    }

    /** Viser dialog for å flytte et arkivdokument. Støtter per nå ikke flytting av feks saksdokument */
    flytt() {
        ArkivFlyttNodeDialogKomponent.visFlyttDokument(this.matDialog, this.dokumentversjon()).beforeClosed();
    }

    async slett() {
        if (erArkivdokument(this.dokumentversjon()) && !(await this.validerAtFilKanSlettes(this.dokumentversjon())))
            return;

        this.avhengigheter.slett(this.dokumentversjon())
    }

    visSkjerming() {
        this.delegerteOperasjoner.visSkjerming(this.dokumentversjon());
    }

    redigerSkjerming() {
        SkjermingDialogKomponent.redigerForDokument(this.matDialog, this.dokumentversjon());
    }

    signer() {
        this.dokumentSigneringTjeneste.signer(this.dokumentversjon(), this.signatarinformasjon());
    }

    async sendTilSignering() {
        await this.dokumentSigneringTjeneste.sendTilSignering(this.dokumentversjon());
    }

    visSignatarer() {
        this.delegerteOperasjoner.visSignatarer(this.dokumentversjon());
    }

    redigerSignatarer() {
        this.dokumentSigneringTjeneste.redigerSignatarer(this.dokumentversjon());
    }

    avsluttSignering() {
        this.dokumentSigneringTjeneste.avsluttSignering(this.dokumentversjon());
    }

    async endreFavorittmarkering() {
        await this.avhengigheter.endreFavorittmarkering(this.dokumentversjon());
    }

    visAlleVersjoner() {
        this.delegerteOperasjoner.visAlleVersjoner(this.dokumentversjon());
    }

    visSakerArkivdokumentBehandlesI() {
        this.delegerteOperasjoner.visSakerArkivdokumentBehandlesI(this.dokumentversjon());
    }

    gaaTilArkivinnslag() {
        this.navigasjon.gåTilArkivdokumentKnyttetTilDokumentVersjon(this.dokumentversjon().id);
    }

    gaaTilProtokoll() {
        this.navigasjon.gåTilMøteProtokollFraProtokolldokumentISak(this.dokumentversjon().dokumentId)
    }

    lastNedSom(format: DokumentFormatInfo) {
       // this.dokumentTjeneste.lastNed(this.dokumentversjon(), format);
    }

    aapneSom(format: DokumentFormatInfo, åpneIPdfjs = true) {
        this.dokumentTjeneste.åpne(this.dokumentversjon(), format, åpneIPdfjs);
    }

    aapneIGoogleDrive() {
        åpneINyttvindu(this.dokumentversjon().googleDriveUrl!);
    }

    aapneIMicrosoftOneDrive() {
        åpneINyttvindu(this.dokumentversjon().microsoftOneDriveUrl!);
    }

    gaaTilSak(sak: TynnSakModell) {
        this.navigasjon.gåTilSak(sak.id);
    }

    //private hjelpemetoder

    private tilgjengeligFunksjonalitet() {
        return this.delegerteOperasjoner.tilgjengeligFunksjonalitet(this.dokumentversjon());
    }

    private velgAgendaDokumentInnhold() {
        AgendaDokumentValgDialogKomponent.vis(this.matDialog, this.dokumentversjon().gruppeId);
    }

    private velgForfalteOppgaverDokumentInnhold() {
        ForfalteOppgaverDokumentValgDialogKomponent.vis(this.matDialog, this.dokumentversjon().gruppeId);
    }

    private async validerAtFilKanSlettes(dokument: ArkivdokumentModell): Promise<boolean> {
        this.kanIkkeSlettesPgaBrukISaker.set(await this.avhengigheter.sakerArkivdokumentBehandlesI(dokument.dokumentId));
        if (erTomListe(this.kanIkkeSlettesPgaBrukISaker())) return true;

        this.matDialog.open(this.dokumentKanIkkeSlettesDialogTemplate(), {width: '500px'})
        return false;
    }

}


/** Operasjoner som dokument-meny-komponenten ikke selv kan realisere, men som noen høyere opp i komponent-treet må realisere */
export interface DelegerteDokumentMenyOperasjoner {

    /** Viser alle versjoner av angitt dokument, se {@link TilgjengeligFunksjonalitet#versjonsvisning}.*/
    visAlleVersjoner(dokumentversjon: DokumentVersjonModell): void;

    /** Viser signeringsstatus for angitt dokument, se {@link TilgjengeligFunksjonalitet#signering}.*/
    visSignatarer(dokumentversjon: DokumentVersjonModell): void;

    /** Viser saker angitt dokument behandles i, se {@link TilgjengeligFunksjonalitet#behandlesISaker}.*/
    visSakerArkivdokumentBehandlesI(dokumentversjon: DokumentVersjonModell): void;

    /** Viser skjermingsstatus for angitt dokument, se {@link TilgjengeligFunksjonalitet#skjerming}.*/
    visSkjerming(dokumentversjon: DokumentVersjonModell): void;

    /** Presenterer bruker med en fil-velger-dialog og registrerer valgt fil som ny versjon av angitt dokument, se {@link TilgjengeligFunksjonalitet#rediger} */
    registrerNyDokumentversjon(dokumentversjon: DokumentVersjonModell): void;

    /** Returnerer informasjon om funksjonalitet som kan skjules for bruker  */
    tilgjengeligFunksjonalitet(dokumentversjon: DokumentVersjonModell): TilgjengeligFunksjonalitet;

}

/** Returnerer en {@link DelegerteDokumentMenyOperasjoner} der all funksjonalitet deaktivert. */
export function delegerteDokumentMenyOperasjonerMedAllFunksjonalitetDeaktivert(): DelegerteDokumentMenyOperasjoner {
    return {
        //tilgjengeligFunksjonalitet.versjonsvisning er satt til false, trenger derfor ikke å implementere denne
        visAlleVersjoner: async dokumentversjon => {
        },

        //tilgjengeligFunksjonalitet.signering er satt til false, trenger derfor ikke å implementere denne
        visSignatarer: async dokumentversjon => {
        },

        //tilgjengeligFunksjonalitet.behandlesISaker er satt til false, trenger derfor ikke å implementere denne
        visSakerArkivdokumentBehandlesI: async dokumentversjon => {
        },

        //tilgjengeligFunksjonalitet.skjerming er satt til false, trenger derfor ikke å implementere denne
        visSkjerming: dokumentversjon => {
        },

        //tilgjengeligFunksjonalitet.rediger er satt til false, trenger derfor ikke å implementere denne
        registrerNyDokumentversjon: dokumentversjon => {
        },

        //returnerer at all funksjonalitet er deaktivert
        tilgjengeligFunksjonalitet: dokumentversjon => {
            return alleFunksjonalitetDeaktivert();
        }
    };
}

export function alleFunksjonalitetAktivert(): TilgjengeligFunksjonalitet {
    return {behandlesISaker: true, favorittmarkering: true, rediger: true, signering: true, versjonsvisning: true, skjerming: true};
}

export function alleFunksjonalitetDeaktivert(): TilgjengeligFunksjonalitet {
    return {behandlesISaker: false, favorittmarkering: false, rediger: false, signering: false, versjonsvisning: false, skjerming: false};
}

/** Definerer hvorvidt spesifikk funksjonalitet er tilgjengelig for bruker for et gitt dokument */
export interface TilgjengeligFunksjonalitet {
    /** Dersom false vises ingen redigerings-relaterte menyinnslag (flytt, slett, ny versjon).
     * Dersom true kalles {@link DelegerteDokumentMenyOperasjoner#registrerNyDokumentversjon} for å vise gui for registrering av ny versjon.
     * Flytt og slett håndteres direkte av {@link DokumentMenyKomponent} */
    rediger: boolean;
    /** Dersom false vises ingen send-til-signering/rediger-signering menyinnslag (men eventuelle signatarer vil fortsatt kunne signere).
     * Dersom true kalles {@link DelegerteDokumentMenyOperasjoner#visSignatarer} for vise signeringsstatus. */
    signering: boolean;
    /** Dersom false vises ingen favoritt-relaterte menyinnslag */
    favorittmarkering: boolean;
    /** Dersom false vises ikke innslag for å se versjoner av dokumentet.
     * Dersom true kalles {@link DelegerteDokumentMenyOperasjoner#visAlleVersjoner} for vise tilgjengelige versjoner. */
    versjonsvisning: boolean;
    /** Dersom false vises ikke innslag for å se behandlinger av arkivdokument i saker,
     * Dersom true kalles {@link DelegerteDokumentMenyOperasjoner#visSakerArkivdokumentBehandlesI} for vise saker dokumentet behandles i. */
    behandlesISaker: boolean;
    /** Dersom false vises ikke innslag for å se eller redigere skjerming.
     * Dersom true kalles {@link DelegerteDokumentMenyOperasjoner#visSkjerming} for vise skjermingsstatus. */
    skjerming: boolean;
}

@Injectable()
export class DokumentMenyKomponentAvhengigheter {

    constructor(private saksdokumentDatatjeneste: SaksdokumentDatatjeneste,
                private arkivDatatjeneste: ArkivDatatjeneste,
                private favorittDatatjeneste: FavorittDatatjeneste,
                private arkivRessurs: ArkivRessursClient) {
    }

    erFavoritt(dokumentversjon: DokumentVersjonModell): boolean {
        return this.favorittDatatjeneste.erFavoritt(dokumentversjon);
    }

    async endreFavorittmarkering(dokumentversjon: DokumentVersjonModell): Promise<void> {
        if (erSaksdokument(dokumentversjon))
            await this.saksdokumentDatatjeneste.endreFavorittmarkering(dokumentversjon);
        if (erArkivdokument(dokumentversjon))
            await this.arkivDatatjeneste.endreFavorittmarkering(dokumentversjon);
    }

    slett(dokumentversjon: DokumentVersjonModell): Promise<void> {
        return erArkivdokument(dokumentversjon) ?
          this.arkivDatatjeneste.slettDokument(dokumentversjon.foreldre.arkivfilnodeId as ArkivnodeId) :
          this.saksdokumentDatatjeneste.slettSaksdokument(dokumentversjon.foreldre.sakId!, dokumentversjon.dokumentId);
    }

    async sakerArkivdokumentBehandlesI(dokumentId: DokumentId): Promise<DokumentVersjonOgSakerModell[]> {
        return this.arkivRessurs.sakerKnyttetTilArkivdokument(dokumentId).somPromise()
    }
}
